'use client';

import { redirect } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect } from 'react';

import usePageView from '@/analytics/hooks/usePageView';
import { usePDPContext } from '@/app/[locale]/product/_contexts/PDPProvider';
import WizardStateProvider from '@/app/[locale]/product/_contexts/WizardStateProvider';
import useCurrentRoute from '@/app/[locale]/product/_hooks/useCurrentRoute';
import type CurrentRouteValues from '@/app/[locale]/product/_hooks/useCurrentRoute/types';
import checkCurrentRoute from '@/app/[locale]/product/_utils/checkCurrentRoute';
import getPdpWizardSeoMetadata from '@/app/[locale]/product/rings/_utils/getPdpWizardSeoMetadata';
import { POP_PATHNAME } from '@/app/consts/pathnames';
import { usePartnerCode } from '@/app/hooks/usePartnerCode';
import usePartnerUtm from '@/app/hooks/usePartnerUtm/usePartnerUtm';

import Layout from './Layout';

interface WizardPageProps {
  readonly accessory?: string;
  readonly children: ReactNode;
  readonly id: string;
  readonly locale: string;
  readonly name: string;
}

const WizardPage = ({
  accessory,
  children,
  id,
  locale,
  name,
}: WizardPageProps) => {
  const t = useTranslations();
  const currentRoute: CurrentRouteValues = useCurrentRoute({
    accessory,
    locale,
  });
  // Update the document title based on the current route
  useEffect(() => {
    if (!currentRoute.style.value || !currentRoute.finish.value) return;
    const { title } = getPdpWizardSeoMetadata(
      currentRoute.style.value,
      currentRoute.finish.value,
    );
    document.title = t(title);
  }, [currentRoute.style.value, currentRoute.finish.value, t]);

  const { error, getFirstStockedStyle, getFirstStockedFinish } =
    usePDPContext();

  // Persist UTM query param data to localStorage (if any params exist)
  usePartnerUtm();

  // Persist partner code query param if exists
  usePartnerCode();

  if (error) {
    redirect(POP_PATHNAME);
  }

  const { isValid, redirectUrl } = checkCurrentRoute({
    ...currentRoute,
    getFirstStockedStyle,
    getFirstStockedFinish,
  });

  if (!isValid && redirectUrl) {
    redirect(redirectUrl);
  }

  usePageView(name);

  return (
    <WizardStateProvider currentRoute={currentRoute}>
      <div data-cy={id}>
        <Layout>{children}</Layout>
      </div>
    </WizardStateProvider>
  );
};

export default WizardPage;
